import { useEffect, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import type { PluginInvocationMessage } from '@atlassian/conversation-assistant-service-api/plugins';

import { isToolName } from './isToolName';
import { IssueSearchToolPluginInvocationsHandler } from './plugins/IssueSearchTool';
import { TOOL_NAMES } from './TOOL_NAMES';

/*
	This file exists as part of the 'rovo_chat_jira_nl_to_jql_minion_in_fe' feature.
	It can be freely deleted if the feature is being removed.
*/

/*
	This type just exists so we can definitively set the name of the plugin
	as a string instead of string | null
	which just saves an extra line of having to check that name is not null
*/
type NamedPluginInvocationMessage = PluginInvocationMessage & {
	definedName: string;
};

export const usePluginInvocationsHandler = (pluginInvocations: PluginInvocationMessage[]): void => {
	// filter for only the ones that we have handlers for
	const filteredPluginInvocations = useMemo(() => {
		// currently only the 'rovo_chat_jira_nl_to_jql_minion_in_fe' feature uses this
		// handler so return immediately if this gate is not active
		if (!fg('rovo_chat_jira_nl_to_jql_minion_in_fe')) {
			return [];
		}

		// using flatmap to both filter out the plugins without names and change the
		// ones with names to type of NamedPluginInvocationMessage
		return pluginInvocations.flatMap((pluginInvocation) => {
			const name = pluginInvocation.plugin_name;

			// at the moment i don't know any other way to clearly identify
			// plugins except by name so any plugins without names will be
			// ignored
			if (!name || !isToolName(name)) {
				return [];
			}

			return {
				definedName: name,
				...pluginInvocation,
			} as NamedPluginInvocationMessage;
		});
	}, [pluginInvocations]);

	useEffect(() => {
		filteredPluginInvocations.forEach((pluginInvocation) => {
			const name = pluginInvocation.definedName;

			switch (name) {
				case TOOL_NAMES.ISSUE_SEARCH:
					IssueSearchToolPluginInvocationsHandler(pluginInvocation);
			}
		});
	}, [filteredPluginInvocations]);
};
